/**
 * Raw HTML source editing for templates authored in 'html' mode.
 *
 * Deliberately dumb: a monospace textarea whose contents are stored VERBATIM as
 * the template body. Nothing here parses, normalises, or re-serialises the
 * markup — that is the whole point, since round-tripping through the TipTap
 * schema is what strips <table> layouts and inline styles. `{{tokens}}` survive
 * exactly as typed because they are never touched either.
 *
 * Exposed as parts (like useRichEditor) so the split editor can put the
 * Insert-field control in the pane header and the textarea in the pane body.
 */
import React from 'react'
import { BUILTIN_TOKENS, groupMergeFields } from '@shared/render'
import { Ic } from './icons'

/** Sentinel option value for the "Add custom field…" entry in both menus. */
export const CUSTOM_FIELD_OPTION = '__add_custom_field__'

/**
 * The "Add custom field…" flow, shared by both Insert-field menus. Inserting
 * the returned token IS its registration — the importer's expected columns,
 * the leads table and the reconciliation gate all derive from the tokens in
 * the saved template, so there is no registry to update.
 */
export function promptForCustomField(): string | null {
  const raw = window.prompt(
    'Name the new field (letters, numbers, underscores).\nIt becomes a column your CSV supplies — e.g. "role".',
  )
  if (!raw) return null
  const token = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
  if (!token) return null
  if (BUILTIN_TOKENS.includes(token)) {
    const ok = window.confirm(
      `{{${token}}} is a built-in token — it is filled from the campaign, the identity, or the lead itself, not from a CSV column of that name. Insert the built-in anyway?`,
    )
    if (!ok) return null
  }
  return token
}

export interface HtmlSourceParts {
  /** drops a literal {{token}} at the caret */
  insertToken: (key: string) => void
  /** the textarea, sized to fill its pane */
  area: React.ReactNode
}

export function useHtmlSource({
  value,
  onChange,
}: {
  value: string
  onChange: (html: string) => void
}): HtmlSourceParts {
  const ref = React.useRef<HTMLTextAreaElement>(null)

  const insertToken = React.useCallback(
    (key: string) => {
      const el = ref.current
      const token = `{{${key}}}`
      if (!el) {
        onChange(value + token)
        return
      }
      const start = el.selectionStart ?? value.length
      const end = el.selectionEnd ?? start
      onChange(value.slice(0, start) + token + value.slice(end))
      // Restore the caret after React re-renders with the new value.
      requestAnimationFrame(() => {
        el.focus()
        const caret = start + token.length
        el.setSelectionRange(caret, caret)
      })
    },
    [value, onChange],
  )

  const area = (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      spellCheck={false}
      wrap="off"
      placeholder={'<table width="600" cellpadding="0" cellspacing="0">…'}
      style={{
        display: 'block',
        flex: 1,
        width: '100%',
        minHeight: 0,
        padding: '12px 14px',
        border: '1px solid var(--border-default)',
        borderRadius: 6,
        outline: 'none',
        resize: 'none',
        background: 'var(--surface-card)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-mono)',
        fontSize: 12,
        lineHeight: 1.6,
        // pre + no wrap: long table rows scroll instead of reflowing
        whiteSpace: 'pre',
        overflow: 'auto',
        boxSizing: 'border-box',
      }}
    />
  )

  return { insertToken, area }
}

/** Accent-soft pill dropdown that inserts a merge token. */
export function InsertFieldButton({
  mergeFields,
  onInsert,
}: {
  mergeFields: string[]
  onInsert: (key: string) => void
}) {
  return (
    <label
      title="Insert field"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 24,
        padding: '0 9px',
        borderRadius: 6,
        background: 'var(--accent-soft)',
        color: 'var(--accent-text)',
        cursor: 'pointer',
        position: 'relative',
        flex: 'none',
      }}
    >
      <Ic.plus size={12} />
      <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-sans)' }}>
        Insert field
      </span>
      <select
        value=""
        onChange={(e) => {
          const k = e.target.value
          if (k === CUSTOM_FIELD_OPTION) {
            const token = promptForCustomField()
            if (token) onInsert(token)
          } else if (k) {
            onInsert(k)
          }
        }}
        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
      >
        <option value="" disabled>
          Insert field
        </option>
        {groupMergeFields(mergeFields).map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.tokens.map((k) => (
              <option key={k} value={k}>
                {`{{${k}}}`}
              </option>
            ))}
          </optgroup>
        ))}
        <option value={CUSTOM_FIELD_OPTION}>＋ Add custom field…</option>
      </select>
    </label>
  )
}
